/**
 * Session state. The token itself is an httpOnly cookie the Server sets, so
 * this only mirrors "is there a session" locally to pick the start screen and
 * to clear the mirror on a 401.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, setUnauthorizedHandler } from './api'
import { bridge } from './bridge'
import { useNavigate } from './router'

const STORAGE_KEY = 'dimeroot.session.present'
const LEGACY_STORAGE_KEY = 'fintech.session.email'

interface SessionValue {
  authenticated: boolean
  signedIn: () => void
  signOut: () => Promise<void>
  /** Clears the local mirror without calling the Server — used after DELETE /me. */
  forget: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null || localStorage.getItem(LEGACY_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(readStored)
  const navigate = useNavigate()

  const forget = useCallback(() => {
    setAuthenticated(false)
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    } catch {
      /* private mode */
    }
  }, [])

  const signedIn = useCallback(() => {
    setAuthenticated(true)
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* private mode */
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.auth.signOut()
    } catch {
      bridge.log('session: sign-out request failed; clearing locally anyway')
    }
    forget()
    navigate('welcome', {}, { replace: true })
  }, [forget, navigate])

  useEffect(() => {
    // A 401 from any screen returns to signIn without an error dialog.
    setUnauthorizedHandler(() => {
      forget()
      navigate('signIn', {}, { replace: true })
    })
    return () => setUnauthorizedHandler(null)
  }, [forget, navigate])

  const value = useMemo<SessionValue>(
    () => ({ authenticated, signedIn, signOut, forget }),
    [authenticated, signedIn, signOut, forget],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession outside SessionProvider')
  return value
}
